import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { FileText, Search, ChevronRight, FileCheck2, FileX2, FileClock, Banknote, ClockAlert, MailCheck } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

export const INV_STATUS = {
  entwurf:    { label: "Entwurf", Icon: FileClock, cls: "border border-slate-200 bg-slate-100 text-slate-600" },
  gestellt:   { label: "Gestellt", Icon: FileCheck2, cls: "border border-violet-200 bg-violet-50 text-violet-700" },
  gesendet:   { label: "Gesendet", Icon: FileCheck2, cls: "border border-blue-200 bg-blue-50 text-blue-700" },
  storniert:  { label: "Storniert", Icon: FileX2, cls: "border border-red-200 bg-red-50 text-red-700" },
};

export const PAY_STATUS = {
  offen:        { label: "Offen",         cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  teilbezahlt:  { label: "Teilbezahlt",   cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400" },
  bezahlt:      { label: "Bezahlt",       cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  ueberzahlt:   { label: "Überzahlt",     cls: "bg-purple-500/10 text-purple-700 dark:text-purple-400" },
  entwurf:      { label: "—",             cls: "bg-muted text-muted-foreground" },
  storniert:    { label: "—",             cls: "bg-muted text-muted-foreground" },
};

const FILTERS = [
  { id: "all", label: "Alle", value: null },
  { id: "open", label: "Offen", value: "open" },
  { id: "overdue", label: "Überfällig", value: "overdue" },
  { id: "entwurf", label: "Entwürfe", value: "entwurf" },
  { id: "gestellt", label: "Gestellt", value: "gestellt" },
  { id: "gesendet", label: "Gesendet", value: "gesendet" },
  { id: "storniert", label: "Storniert", value: "storniert" },
];

export default function Invoices() {
  const [statusFilter, setStatusFilter] = useState(null);
  const [q, setQ] = useState("");
  const { data = [] } = useQuery({
    queryKey: ["invoices", statusFilter],
    queryFn: async () => (await api.get("/invoices", { params: {
      status: statusFilter && !["open", "overdue"].includes(statusFilter) ? statusFilter : undefined,
    }})).data,
  });
  const today = new Date().toISOString().slice(0, 10);
  const byFinancialState = data.filter((invoice) => {
    if (statusFilter === "open") return ["gestellt", "gesendet"].includes(invoice.status) && invoice.balance_due > 0.005;
    if (statusFilter === "overdue") return ["gestellt", "gesendet"].includes(invoice.status) && invoice.balance_due > 0.005 && invoice.due_date && invoice.due_date < today;
    return true;
  });
  const filtered = q
    ? byFinancialState.filter((i) => {
        const s = q.toLowerCase();
        return (i.invoice_number || "").toLowerCase().includes(s)
          || (i.customer_snapshot?.name || "").toLowerCase().includes(s)
          || (i.sent_to || "").toLowerCase().includes(s);
      })
    : byFinancialState;

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading stellar-heading-violet flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="stellar-module-icon stellar-module-violet"><FileText /></div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Rechnungen</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Rechnungserstellung, Nummerierung RE-YYYY-####.</p>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Nummer / Kunde…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full sm:w-64 bg-white" data-testid="invoice-search" />
          </div>
        </div>
      </div>

      <div className="stellar-segmented mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.value)}
            data-testid={`invoice-filter-${f.id}`}
            className={`px-3.5 py-1.5 text-sm rounded-lg whitespace-nowrap ${statusFilter === f.value ? "bg-white text-violet-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="premium-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <div className="mb-1">Keine Rechnungen.</div>
            <div className="text-xs">Rechnungen entstehen durch Umwandlung eines Auftrags.</div>
          </div>
        ) : (
          <table className="stellar-data-table w-full text-sm">
            <thead className="bg-slate-50/80 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Nummer</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Kunde</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Fahrzeug</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Zahlung</th>
                <th className="text-right px-4 py-3">Summe</th>
                <th className="text-right px-4 py-3 hidden lg:table-cell">Offen</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Fällig</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((i) => {
                const st = INV_STATUS[i.status] || INV_STATUS.entwurf;
                const ps = PAY_STATUS[i.payment_status] || PAY_STATUS.offen;
                const showPay = ["gestellt", "gesendet"].includes(i.status);
                const overdue = showPay && i.balance_due > 0.005 && i.due_date && i.due_date < today;
                return (
                  <tr key={i.id} className="stellar-table-row">
                    <td className="px-4 py-3">
                      <Link to={`/rechnungen/${i.id}`} data-testid={`invoice-row-${i.id}`} className="font-mono font-medium hover:underline">
                        {i.invoice_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{i.customer_snapshot?.name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{i.vehicle_snapshot?.display || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${st.cls}`}>
                        <st.Icon className="h-3 w-3" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {showPay ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${ps.cls}`} data-testid={`invoice-paystatus-${i.id}`}>
                          <Banknote className="h-3 w-3" /> {ps.label}
                        </span>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                      {i.sent_at && <div className="mt-1 flex items-center gap-1 text-[10px] text-blue-600"><MailCheck className="h-3 w-3" /> versendet</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{fmtEur(i.total)}</td>
                    <td className="px-4 py-3 text-right hidden lg:table-cell text-xs">
                      {showPay && i.balance_due > 0.005 ? (
                        <span className="text-amber-700 dark:text-amber-400 font-medium">{fmtEur(i.balance_due)}</span>
                      ) : showPay ? (
                        <span className="text-emerald-700 dark:text-emerald-400">✓</span>
                      ) : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right text-xs hidden md:table-cell ${overdue ? "font-semibold text-red-600" : "text-muted-foreground"}`}>
                      {overdue && <ClockAlert className="mr-1 inline h-3.5 w-3.5" />}{fmtDate(i.due_date)}
                    </td>
                    <td className="px-2"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
